import React, { useState, useMemo, useCallback } from 'react';
import {
  View,
  RefreshControl,
  FlatList,
  StyleSheet,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native';

import { TabView, SceneMap, TabBar } from 'react-native-tab-view';

import { TopMenu, RatingModal, PushNotificationsModal, Text } from '_/components';
import ContainerWidthLimit from '_/components/ContainerWidthLimit';
import { VisitItem } from '_/components/VisitItem';
import { DeliveryItem } from '_/components/DeliveryItem';

import { EmptyActivities } from '_/components/EmptyActivities';
import { EmptyDeliveries } from '_/components/EmptyDeliveries';

import themes from '_/constants/themes';
import { useVisitsContext } from '_/hooks/VisitsProvider';
import { useDeliveriesContext } from '_/hooks/DeliveriesProvider';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { fontFamily } from '_/config/theme';
import { VisitAuthorizationStatusType } from '@w3lcome/types';

export default function Home() {
  const layout = useWindowDimensions();

  const { t: translate, i18n } = useTranslation();
  const { loading } = useSelector((state: any) => state.loading);

  const { visitsToday, loading: loadingVisits, getCheckinVisits } = useVisitsContext();

  const { getDeliveriesToday, deliveriesToday } = useDeliveriesContext();

  const deliveriesEnabled = useSelector(
    (state: any) => state.company?.customization.deliveriesEnabled
  );

  const [index, setIndex] = useState(0);

  const routes = useMemo(() => {
    const baseRoutes = [{ key: 'visitors', title: translate('visitors') }];

    if (deliveriesEnabled) {
      baseRoutes.push({ key: 'deliveries', title: translate('general.deliveries') });
    }

    return baseRoutes;
  }, [deliveriesEnabled, i18n.language, translate]);

  const [refreshing, setRefreshing] = useState(false);

  const [statusFilter, setStatusFilter] = useState<'all' | 'approved' | 'pending' | 'denied'>(
    'all'
  );

  const sortedVisits = useMemo(() => {
    return [...visitsToday].sort((a, b) => +b.date - +a.date);
  }, [visitsToday]);

  const filteredVisits = useMemo(() => {
    if (statusFilter === 'approved') {
      return sortedVisits.filter(
        (v) => v.authorizationStatus === VisitAuthorizationStatusType.ALLOWED
      );
    }

    if (statusFilter === 'pending') {
      return sortedVisits.filter(
        (v) => v.authorizationStatus === VisitAuthorizationStatusType.AWAITING
      );
    }

    if (statusFilter === 'denied') {
      return sortedVisits.filter(
        (v) => v.authorizationStatus === VisitAuthorizationStatusType.DENIED
      );
    }

    return sortedVisits;
  }, [sortedVisits, statusFilter]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);

    if (index === 0) {
      getCheckinVisits();
    } else {
      getDeliveriesToday();
    }

    setTimeout(() => setRefreshing(false), 1000);
  }, [index]);

  const getFilterColor = (type: string) => {
    switch (type) {
      case 'approved':
        return themes.colors.success;
      case 'pending':
        return themes.colors.warning;
      case 'denied':
        return themes.colors.danger;
      default:
        return themes.colors.primary;
    }
  };

  const FilterChip = ({ value }: { value: 'all' | 'approved' | 'pending' | 'denied' }) => {
    const active = statusFilter === value;
    const baseColor = getFilterColor(value);

    return (
      <TouchableOpacity
        onPress={() => setStatusFilter(value)}
        style={[
          styles.chip,
          {
            backgroundColor: active ? baseColor : `${baseColor}20`,
          },
        ]}
      >
        <Text
          style={[
            styles.chipText,
            {
              color: active ? '#fff' : baseColor,
            },
          ]}
        >
          {translate(`${value}`)}
        </Text>
      </TouchableOpacity>
    );
  };

  const VisitorsRoute = () => (
    <View style={{ flex: 1 }}>
      <View style={styles.filtersContainer}>
        <FilterChip value="all" />
        <FilterChip value="approved" />
        <FilterChip value="pending" />
        <FilterChip value="denied" />
      </View>

      <FlatList
        data={filteredVisits}
        keyExtractor={(item) => item.id.toString()}
        renderItem={({ item }) => <VisitItem visit={item} />}
        ListEmptyComponent={<EmptyActivities filter={statusFilter} />}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          flexGrow: 1,
          paddingHorizontal: 16,
        }}
      />
    </View>
  );

  const DeliveriesRoute = () => (
    <FlatList
      data={deliveriesToday}
      keyExtractor={(item) => item.id.toString()}
      renderItem={({ item }) => <DeliveryItem delivery={item} />}
      ListEmptyComponent={<EmptyDeliveries />}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{
        flexGrow: 1,
        paddingHorizontal: 16,
      }}
    />
  );

  const renderScene = SceneMap({
    visitors: VisitorsRoute,
    deliveries: DeliveriesRoute,
  });

  const renderTabBar = (props: any) => (
    <TabBar
      {...props}
      indicatorStyle={{
        backgroundColor: themes.colors.primary,
        height: 3,
        borderRadius: 3,
      }}
      style={{
        backgroundColor: themes.colors.lightQuaternary,
        elevation: 0,
        shadowOpacity: 0,
      }}
      labelStyle={{
        fontFamily: fontFamily.semibold,
        textTransform: 'none',
        fontSize: 14,
      }}
      activeColor={themes.colors.primary}
      inactiveColor={themes.colors.graySecondary}
    />
  );

  const todayFormatted = new Date().toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  return (
    <ContainerWidthLimit>
      <View style={styles.container}>
        <TopMenu />

        <View style={styles.headerContainer}>
          <Text style={styles.sectionTitle}>{translate('todayActivities')}</Text>

          <Text style={styles.sectionSubtitle}>{todayFormatted}</Text>
        </View>

        <TabView
          navigationState={{ index, routes }}
          renderScene={renderScene}
          onIndexChange={(i) => {
            setIndex(i);

            if (i === 1 && deliveriesEnabled) {
              getDeliveriesToday();
            }
          }}
          initialLayout={{ width: layout.width }}
          renderTabBar={renderTabBar}
        />

        {!loading && !loadingVisits && <PushNotificationsModal />}
        <RatingModal />
      </View>
    </ContainerWidthLimit>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: themes.colors.lightQuaternary,
  },
  headerContainer: {
    backgroundColor: themes.colors.lightQuaternary,
    paddingTop: 8,
    paddingHorizontal: 20,
    paddingBottom: 20,
    borderBottomLeftRadius: 28,
    borderBottomRightRadius: 28,
  },
  sectionTitle: {
    fontSize: 22,
    fontFamily: fontFamily.semibold,
    color: themes.colors.grayPrimary,
  },
  sectionSubtitle: {
    fontSize: 13,
    color: themes.colors.graySecondary,
    marginTop: 4,
    fontFamily: fontFamily.semibold,
  },
  filtersContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 8,
    alignItems: 'center',
  },
  chip: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 10,
    marginRight: 8,
    marginBottom: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.1,
    shadowRadius: 6,
  },
  chipText: {
    fontSize: 12.5,
    fontFamily: fontFamily.semibold,
  },
});
